import React, { useState } from 'react';
import RBForm from 'react-bootstrap/Form';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import { DatePicker, Select } from '@fikasio/react-ui-components';
import { RecurrenceFrequency, RecurrenceRule } from 'services/tasks/types';
import { pickedDateTimeInUserZone, toJSDate, toPickerDate } from 'utils/date';
import styles from './TaskRecurrenceFields.module.css';

const WEEKDAYS = [
  { labelKey: 'monday', value: 1 },
  { labelKey: 'tuesday', value: 2 },
  { labelKey: 'wednesday', value: 3 },
  { labelKey: 'thursday', value: 4 },
  { labelKey: 'friday', value: 5 },
  { labelKey: 'saturday', value: 6 },
  { labelKey: 'sunday', value: 0 },
] as const;

const WEEKDAY_NTHS = [
  { labelKey: 'ordinalFirst', value: 1 },
  { labelKey: 'ordinalSecond', value: 2 },
  { labelKey: 'ordinalThird', value: 3 },
  { labelKey: 'ordinalFourth', value: 4 },
  { labelKey: 'ordinalFifth', value: 5 },
  { labelKey: 'last', value: -1 },
] as const;

type EndsMode = 'never' | 'onDate' | 'after';
type OnMode = 'day' | 'weekday';
type RecurrencePreset = 'daily' | 'weekly' | 'monthly' | 'yearly' | 'custom';

const DEFAULT_RULE: RecurrenceRule = {
  frequency: 'daily',
  interval: 1,
};

const presetOf = (rule?: RecurrenceRule): RecurrencePreset => {
  if (!rule) {
    return 'daily';
  }
  if (rule.frequency === 'daily') {
    return 'daily';
  }
  if (rule.frequency === 'yearly') {
    return 'yearly';
  }
  if ((rule.interval ?? 1) > 1) {
    return 'custom';
  }
  if (rule.frequency === 'weekly') {
    return 'weekly';
  }
  if (rule.frequency === 'monthly') {
    return 'monthly';
  }
  return 'daily';
};

const omitUnusedRuleFields = (rule: RecurrenceRule): RecurrenceRule => {
  const next: RecurrenceRule = {
    frequency: rule.frequency,
    interval: rule.interval && rule.interval > 0 ? rule.interval : 1,
  };
  const usesNthWeekday = (
    rule.frequency === 'monthly'
    && rule.weekdayNth != null
    && Boolean(rule.daysOfWeek?.length)
  );
  if (usesNthWeekday) {
    next.weekdayNth = rule.weekdayNth;
    next.daysOfWeek = rule.daysOfWeek;
  } else if (rule.frequency === 'monthly' && rule.dayOfMonth) {
    next.dayOfMonth = rule.dayOfMonth;
  } else if (
    (rule.frequency === 'weekly' || rule.frequency === 'monthly')
    && rule.daysOfWeek?.length
  ) {
    next.daysOfWeek = rule.daysOfWeek;
  }
  if (rule.endDate) {
    next.endDate = rule.endDate;
  }
  if (rule.count) {
    next.count = rule.count;
  }
  return next;
};

interface TaskRecurrenceFieldsProps {
  anchorDate?: Date | string | null;
  onChange: (recurrence: RecurrenceRule[]) => void;
  recurrence?: RecurrenceRule[];
}

const TaskRecurrenceFields = ({
  anchorDate,
  onChange,
  recurrence,
}: TaskRecurrenceFieldsProps) => {
  const { t } = useTranslation();
  const rule = recurrence?.[0];
  const defaultDayOfMonth = (
    toJSDate(anchorDate) ? DateTime.fromJSDate(toJSDate(anchorDate)!).day : DateTime.now().day
  );
  const hasRecurrence = Boolean(rule);
  const [preset, setPreset] = useState<RecurrencePreset>(presetOf(rule));
  const [endsMode, setEndsMode] = useState<EndsMode>(
    rule?.endDate ? 'onDate' : rule?.count ? 'after' : 'never',
  );
  const onMode: OnMode = rule?.weekdayNth != null ? 'weekday' : 'day';
  const selectedWeekday = rule?.daysOfWeek?.[0] ?? 1;

  const updateRule = (patch: Partial<RecurrenceRule> | null) => {
    if (patch === null) {
      onChange([]);
      return;
    }
    onChange([omitUnusedRuleFields({
      ...DEFAULT_RULE,
      ...rule,
      ...patch,
    })]);
  };

  const applyPreset = (nextPreset: RecurrencePreset) => {
    setPreset(nextPreset);
    if (nextPreset === 'daily') {
      updateRule({
        frequency: 'daily',
        interval: 1,
        daysOfWeek: undefined,
        dayOfMonth: undefined,
        weekdayNth: undefined,
      });
      return;
    }
    if (nextPreset === 'weekly') {
      updateRule({
        frequency: 'weekly',
        interval: 1,
        dayOfMonth: undefined,
        weekdayNth: undefined,
      });
      return;
    }
    if (nextPreset === 'monthly') {
      updateRule({
        frequency: 'monthly',
        interval: 1,
        daysOfWeek: undefined,
        weekdayNth: undefined,
        dayOfMonth: rule?.dayOfMonth ?? defaultDayOfMonth,
      });
      return;
    }
    if (nextPreset === 'yearly') {
      updateRule({
        frequency: 'yearly',
        interval: 1,
        daysOfWeek: undefined,
        dayOfMonth: undefined,
        weekdayNth: undefined,
      });
      return;
    }
    updateRule({
      frequency: rule?.frequency === 'monthly' ? 'monthly' : 'weekly',
      interval: Math.max(2, rule?.interval ?? 2),
      dayOfMonth: undefined,
      weekdayNth: undefined,
    });
  };

  const toggleDay = (day: number) => {
    const days = rule?.daysOfWeek ?? [];
    updateRule({
      daysOfWeek: days.includes(day)
        ? days.filter(value => value !== day)
        : [...days, day],
    });
  };

  const weekdayChecks = (
    <div className={styles.days}>
      {
        WEEKDAYS.map(day => (
          <RBForm.Check
            checked={Boolean(rule?.daysOfWeek?.includes(day.value))}
            id={`recurrence-day-${day.value}`}
            key={day.value}
            label={t(day.labelKey)}
            onChange={() => toggleDay(day.value)}
            type="checkbox"
          />
        ))
      }
    </div>
  );

  return (
    <div>
      <div className={styles.header}>
        <label className={`form-label ${styles.headerLabel}`}>
          {t('recurrence')}
        </label>
        <RBForm.Check
          aria-label={t('recurrence')}
          checked={hasRecurrence}
          onChange={event => {
            if (event.target.checked) {
              setPreset('daily');
              setEndsMode('never');
              updateRule(DEFAULT_RULE);
            } else {
              updateRule(null);
            }
          }}
          type="switch"
        />
      </div>
      {
        hasRecurrence && rule && (
          <>
            <div className={styles.row}>
              <Select
                onChange={value => applyPreset(value as RecurrencePreset)}
                options={[
                  { label: t('daily'), value: 'daily' },
                  { label: t('weekly'), value: 'weekly' },
                  { label: t('monthly'), value: 'monthly' },
                  { label: t('yearly'), value: 'yearly' },
                  { label: t('custom'), value: 'custom' },
                ]}
                style={{ flex: 1, minWidth: 0 }}
                value={preset}
              />
            </div>
            {
              preset === 'weekly' && (
                <>
                  <label className="form-label">{t('daysOfWeek')}</label>
                  {weekdayChecks}
                </>
              )
            }
            {
              preset === 'monthly' && (
                <>
                  <label className="form-label">{t('onThe')}</label>
                  <div className={styles.row}>
                    <Select
                      onChange={value => {
                        const mode = value as OnMode;
                        if (mode === 'weekday') {
                          updateRule({
                            weekdayNth: 1,
                            daysOfWeek: [1],
                            dayOfMonth: undefined,
                          });
                          return;
                        }
                        updateRule({
                          weekdayNth: undefined,
                          daysOfWeek: undefined,
                          dayOfMonth: rule?.dayOfMonth ?? defaultDayOfMonth,
                        });
                      }}
                      options={[
                        { label: t('dayOfMonth'), value: 'day' },
                        { label: t('nthWeekday'), value: 'weekday' },
                      ]}
                      style={{ flex: 1, minWidth: 0 }}
                      value={onMode}
                    />
                  </div>
                  {
                    onMode === 'day' && (
                      <input
                        aria-label={t('dayOfMonth')}
                        className={`form-control ${styles.numberInput}`}
                        max={31}
                        min={1}
                        onChange={event => {
                          const value = Number(event.target.value);
                          updateRule({
                            dayOfMonth: Number.isNaN(value) || value < 1
                              ? undefined
                              : Math.min(31, Math.floor(value)),
                            weekdayNth: undefined,
                            daysOfWeek: undefined,
                          });
                        }}
                        type="number"
                        value={rule.dayOfMonth ?? ''}
                      />
                    )
                  }
                  {
                    onMode === 'weekday' && (
                      <div className={styles.row}>
                        <Select
                          onChange={value => updateRule({
                            weekdayNth: Number(value),
                          })}
                          options={WEEKDAY_NTHS.map(nth => ({
                            label: t(nth.labelKey),
                            value: String(nth.value),
                          }))}
                          style={{ flex: 1, minWidth: 0 }}
                          value={String(rule.weekdayNth ?? 1)}
                        />
                        <Select
                          onChange={value => updateRule({
                            daysOfWeek: [Number(value)],
                          })}
                          options={WEEKDAYS.map(day => ({
                            label: t(day.labelKey),
                            value: String(day.value),
                          }))}
                          style={{ flex: 1, minWidth: 0 }}
                          value={String(selectedWeekday)}
                        />
                      </div>
                    )
                  }
                </>
              )
            }
            {
              preset === 'custom' && (
                <>
                  <label className="form-label">{t('repeatEvery')}</label>
                  <div className={styles.row}>
                    <input
                      aria-label={t('repeatEvery')}
                      className={`form-control ${styles.rowField}`}
                      min={1}
                      onChange={event => updateRule({
                        interval: Math.max(1, Number(event.target.value) || 1),
                      })}
                      type="number"
                      value={rule.interval ?? 2}
                    />
                    <Select
                      onChange={value => updateRule({
                        frequency: value as RecurrenceFrequency,
                        dayOfMonth: undefined,
                        weekdayNth: undefined,
                      })}
                      options={[
                        { label: t('week'), value: 'weekly' },
                        { label: t('month'), value: 'monthly' },
                      ]}
                      style={{ flex: 1, minWidth: 0 }}
                      value={rule.frequency === 'monthly' ? 'monthly' : 'weekly'}
                    />
                  </div>
                  <label className="form-label">{t('daysOfWeek')}</label>
                  {weekdayChecks}
                </>
              )
            }
            <label className="form-label">{t('ends')}</label>
            <div className={styles.row}>
              <Select
                onChange={value => {
                  const mode = value as EndsMode;
                  setEndsMode(mode);
                  if (mode === 'never') {
                    updateRule({ count: undefined, endDate: undefined });
                  } else if (mode === 'onDate') {
                    updateRule({ count: undefined });
                  } else {
                    updateRule({ count: 1, endDate: undefined });
                  }
                }}
                options={[
                  { label: t('never'), value: 'never' },
                  { label: t('onDate'), value: 'onDate' },
                  { label: t('after'), value: 'after' },
                ]}
                style={{ flex: 1, minWidth: 0 }}
                value={endsMode}
              />
              {
                endsMode === 'onDate' && (
                  <DatePicker
                    className={`form-control ${styles.rowField}`}
                    displayFormat="yyyy-MM-dd"
                    onChange={date => {
                      const timestamp = pickedDateTimeInUserZone(date)
                        .set({ hour: 23, minute: 59, second: 59, millisecond: 999 });
                      updateRule({ endDate: timestamp.toISO() ?? undefined });
                    }}
                    value={toPickerDate(rule.endDate)}
                  />
                )
              }
              {
                endsMode === 'after' && (
                  <>
                    <input
                      aria-label={t('occurrences')}
                      className={`form-control ${styles.rowField}`}
                      min={1}
                      onChange={event => updateRule({
                        count: Math.max(1, Number(event.target.value) || 1),
                      })}
                      type="number"
                      value={rule.count ?? 1}
                    />
                    <span>{t('occurrences')}</span>
                  </>
                )
              }
            </div>
          </>
        )
      }
    </div>
  );
};

export default TaskRecurrenceFields;
